// SEO-12 · /resources/designing-a-school-fee-structure
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Fee heads with their own HSN/SAC + GST rate; amounts are GST-INCLUSIVE and the split is
//     back-computed on read so the payable total never changes — `core/fee_gst.py`.
//   • Installment plans are a planning/reminder aid with covered/due/overdue DERIVED from the
//     invoice's cumulative paid — never a parallel ledger (`fee_installment_plans`, CLAUDE.md
//     "Payment plans / installments").
//   • Fines: configurable flat/percent with grace period and cap, applied as a reconciled invoice
//     line, idempotent per rule per invoice (`core/fines.py`).
//   • Demand generation: monthly/quarterly structures bill the current period through the golden
//     create_student_invoice path, idempotent on `invoices.billing_period` + a partial-unique
//     (tenant, structure, student, period) index — opt-in per school (P4).
//   • Invoice numbers come from the atomic COUNTERS register, never count(*)+1 (CLAUDE.md rails).
//   • Voided ⇒ financially inert; a part-paid past-due invoice stays `overdue`
//     (`core/invoice_status.py`).
// NO invented fee benchmarks, no advice on what a school should charge, no tax position.

const article = {
  slug: "designing-a-school-fee-structure",
  title: "Designing a fee structure you will not have to unpick in January",
  seoTitle: "Designing a School Fee Structure | Vidyapeeth360",
  description:
    "Heads, categories, instalments and fines: the fee-structure decisions that are cheap in March and expensive to reverse halfway through the year.",
  topic: "Fees & finance",
  topicKey: "fees",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "The fee structure is the one configuration a school cannot quietly fix mid-year: every change has to be explained to a parent who already paid. Which makes the half hour you spend on its shape the highest-leverage half hour of the year.",
  moduleLinks: ["fee-structure", "installment-setup", "fine-management"],
  conversion: {
    to: "/pricing",
    label: "See plans and the full cost schedule",
    note: "Subscription, gateway charges and per-message rates are published, so you can model the running cost yourself.",
  },
  sections: [
    {
      id: "heads",
      heading: "Start with heads, not with totals",
      blocks: [
        {
          t: "p",
          text: "The most common structure in an Indian school is one number per class per term, with the breakdown living in a circular. It works until the first question arrives: a family paying only transport, a sibling on a tuition-only concession, an auditor asking how much of last year's collection was hostel income.",
        },
        {
          t: "p",
          text: "Split the fee into named heads from the start — tuition, admission, examination, transport, hostel, library, activity — even where every student in a class pays all of them. A head is not extra work; it is the unit that everything downstream reads.",
        },
        {
          t: "ul",
          items: [
            "**Reporting** becomes possible: income by head, comparable across terms, tied to the accounts rather than assembled by hand.",
            "**Partial billing** becomes possible: a day scholar who takes the bus, a boarder who does not.",
            "**Relief** becomes precise: a concession applies to tuition without silently discounting the transport the school still has to pay for.",
            "**Tax treatment differs by head** — and a single blended number makes that impossible to represent honestly.",
          ],
        },
      ],
    },
    {
      id: "categories",
      heading: "Categories beat exceptions",
      blocks: [
        {
          t: "p",
          text: "Every school has students who do not pay the standard amount: staff wards, siblings, RTE admissions, scholarship holders, a trustee's decision. There are two ways to represent them and only one of them survives.",
        },
        {
          t: "p",
          text: "The wrong way is to edit the individual invoice. The right way is to define the category once, price it once, and let the invoice be generated correctly — with genuinely individual decisions handled as an approved concession posted on top. The test is simple: if you cannot answer \"how many students pay the sibling rate, and what did that cost us?\" without opening invoices one by one, the exceptions are in the wrong place.",
        },
      ],
    },
    {
      id: "instalments",
      heading: "Instalments: a schedule, not a second ledger",
      blocks: [
        {
          t: "p",
          text: "Families ask to pay in parts, and schools reasonably agree. The trap is building the instalment plan as its own set of records, which immediately creates two versions of what is owed — and they diverge the first time somebody pays an odd amount, or pays twice, or pays late.",
        },
        {
          t: "p",
          text: "Keep one invoice as the truth and let the plan be a view over it: dated milestones whose covered, due and overdue states are derived from what the invoice has actually received. Then a part payment updates one number, the plan re-derives, and there is nothing to reconcile between the two.",
        },
        {
          t: "note",
          title: "A related decision: what \"overdue\" means",
          text: "An invoice that is past its due date and part-paid is still overdue — it is not \"partly paid\" in a way that removes it from the chase list. Fix the vocabulary before the year starts, because every reminder rule, KPI and report will inherit it.",
        },
      ],
    },
    {
      id: "fines",
      heading: "Late fees you can defend",
      blocks: [
        {
          t: "p",
          text: "A late fee is a policy that gets applied thousands of times without a human looking, so it needs to be boring and bounded.",
        },
        {
          t: "ol",
          items: [
            "**Choose flat or percentage, and write down which.** A percentage on a large annual invoice compounds into a number no principal wants to defend in a meeting.",
            "**Give it a grace period.** Payments cross weekends and bank holidays; a fine that lands on day one converts an administrative delay into a grievance.",
            "**Cap it.** An uncapped daily fine on an unpaid term fee eventually exceeds the fee, at which point the school stops applying it and the ledger stops meaning anything.",
            "**Apply it as a line, once.** A fine is an invoice line with its own reason, applied one time per rule per invoice — never a silently increased total, and never applied twice because a job ran again.",
            "**Preview before you commit.** Fines are applied in batches. Seeing the list and the total before it lands is the difference between a policy and an incident.",
          ],
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "A fee structure is built from heads, each carrying its own HSN/SAC and rate, and priced by class and category. Amounts are treated as inclusive of any tax that applies, so what the parent sees on the notice is what the parent pays; the split is back-computed for the records rather than added on top. Your school's own tax position stays a question for your adviser.",
        },
        {
          t: "p",
          text: "Invoices carry their heads as lines, with concessions posted as negative lines against them, so the invoice always shows both what was billed and what was relieved. Invoice and receipt numbers come from one atomic per-school register — never from counting existing rows, which collides the moment two are raised at once or one is cancelled. Voiding an invoice makes it financially inert rather than merely marked: it cannot be paid, and it stops counting towards outstanding.",
        },
        {
          t: "p",
          text: "Instalment plans are a derived planning aid over the invoice's own cumulative payments, not a second ledger — so a part payment cannot make the plan and the invoice disagree. Fines are configured as rules with a grace period and a cap, previewed as a list with its total, and applied as a reconciled invoice line, idempotent per rule per invoice.",
        },
        {
          t: "p",
          text: "Where a school bills monthly or quarterly, demand generation is an opt-in that raises the current period's invoices through the same path a manual invoice takes, keyed on the billing period so a re-run cannot double-bill a family. That is the whole feature: [fee structures](/modules/fee-structure) that generate correctly, [instalments](/modules/installment-setup) that derive, and [fines](/modules/fine-management) that stay bounded.",
        },
        {
          t: "p",
          text: "If you are setting this up for the first time, spend the time on heads and categories. Instalments and fines are settings you can change in October; the shape of the bill is not.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
